import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { ERROR_SEND_MESSAGE_SERVER } from '@/app/module/logs/definitions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

type SendMessageParams = {
  contactIds: number[]
  excludeIds?: number[]
  includedGroupIds?: number[]
  isAllContacts?: boolean
  message: string
  search?: string
  senderId?: string
}

export const useSendMessage = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [sendOrgSmsContacts, result] = esApi.endpoints.sendOrgSmsContacts.useMutation()

  const sendMessage = useCallback(
    async (params: SendMessageParams) => {
      if (!orgId) {
        return false
      }

      const notificationKey = `send-message-${params.contactIds.join('-')}`
      try {
        const response = await sendOrgSmsContacts({
          orgId: String(orgId),
          body: {
            contacts: {
              allContactsAreSelected: params.isAllContacts,
              includedContactIds: params.contactIds,
              excludedContactIds: params.excludeIds,
              includedGroupIds: params.includedGroupIds,
              search: params.search,
            },
            from: params.senderId,
            message: params.message,
          },
        }).unwrap()
        const failedMessages = response.messages?.filter((m) => !!m.error) ?? []
        const hasError = !!response.error
        const notiType = hasError || failedMessages.length ? 'warning' : 'success'
        let message = hasError ? 'Invalid/deleted contact or not enough balance.' : 'Message successfully sent.'
        if (failedMessages.length) {
          message = 'Some messages failed to go out. Please contact support for more details.'
        }
        dispatch(createNotification({ [notificationKey]: { type: notiType, message } }))
        return true
      } catch (err) {
        const message = getErrorMessage(err, ERROR_SEND_MESSAGE_SERVER)
        dispatch(createNotification({ [notificationKey]: { type: 'error', message } }))
        return false
      }
    },
    [dispatch, orgId, sendOrgSmsContacts],
  )

  return [sendMessage, result] as const
}

export default useSendMessage
